import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Waves, ArrowRight, ArrowLeft, Compass, Sparkles } from "lucide-react";
import { ROOMS, PROPERTY_SPOTS } from "../../data";

export default function RoomTour({ onNext }) {
    const [tab, setTab] = useState("inside");
    const [idx, setIdx] = useState(0);
    const items = tab === "inside" ? ROOMS : PROPERTY_SPOTS;
    const current = items[Math.min(idx, items.length - 1)];

    const switchTab = (t) => { setTab(t); setIdx(0); };

    return (
        <div data-testid="room-tour-section">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold">Step one</p>
                    <h3 className="font-serif-d font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F2837] mt-2">
                        Explore the villa, <em className="text-[#2D6A76]">room by room.</em>
                    </h3>
                </div>
                <div className="inline-flex rounded-full bg-[#F3EDE2] p-1.5 border border-[#E5DCCB]" data-testid="room-tour-tabs">
                    {[["inside", "Inside Villa"], ["outside", "Around the Property"]].map(([id, label]) => (
                        <button key={id} onClick={() => switchTab(id)} data-testid={`room-tab-${id}`}
                            className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "text-white" : "text-[#5C6A78] hover:text-[#0F2837]"}`}>
                            {tab === id && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-[#0F2837]" transition={{ type: "spring", stiffness: 300, damping: 28 }} />}
                            <span className="relative z-10">{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 lg:gap-10 items-stretch">
                <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_60px_-25px_rgba(15,40,55,0.4)] min-h-[340px] lg:min-h-[480px]">
                    <AnimatePresence mode="wait">
                        <motion.img key={current.id + tab} src={current.img} alt={current.label}
                            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute inset-0 w-full h-full object-cover" data-testid="room-tour-image" />
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2837]/75 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C49A45] font-semibold">{tab === "inside" ? "Inside the villa" : "Around the property"}</p>
                        <h4 className="font-serif-d text-3xl text-white mt-1" data-testid="room-tour-title">{current.label}</h4>
                        <p className="text-white/85 text-sm mt-2 max-w-md leading-relaxed" data-testid="room-tour-benefit">{current.benefit}</p>
                    </div>
                    <div className="absolute top-5 right-5 flex gap-2">
                        <button onClick={() => setIdx((idx - 1 + items.length) % items.length)} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition" data-testid="room-tour-prev" aria-label="Previous"><ArrowLeft size={17} /></button>
                        <button onClick={() => setIdx((idx + 1) % items.length)} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition" data-testid="room-tour-next" aria-label="Next"><ArrowRight size={17} /></button>
                    </div>
                </div>

                <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:max-h-[480px] pb-2 lg:pb-0 lg:pr-1" data-testid="room-list">
                    {items.map((item, i) => (
                        <button key={item.id} onClick={() => setIdx(i)} data-testid={`room-item-${item.id}`}
                            className={`shrink-0 lg:shrink flex items-center gap-4 rounded-2xl border p-3 text-left transition-all duration-300 min-w-[220px] lg:min-w-0 ${i === Math.min(idx, items.length - 1) ? "border-[#C49A45] bg-white shadow-lg" : "border-[#E5DCCB] bg-white/60 hover:bg-white"}`}>
                            <img src={item.img} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" loading="lazy" />
                            <span>
                                <span className="block font-semibold text-[#0F2837] text-sm">{item.label}</span>
                                <span className="block text-[11px] text-[#5C6A78] mt-0.5 line-clamp-2">{item.benefit}</span>
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-8 flex justify-end">
                <button onClick={onNext} className="inline-flex items-center gap-2 text-sm font-semibold text-[#2D6A76] hover:gap-3 transition-all" data-testid="room-tour-continue">
                    Now explore the island <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
}

export function IslandMarquee() {
    return (
        <div className="py-5 border-y border-[#E5DCCB] bg-[#F3EDE2] my-16" data-testid="island-experiences-marquee">
            <div className="overflow-hidden flex items-center gap-8 marquee-track">
                {[...Array(2)].flatMap((_, k) => ["Explore the property", "Discover Amelia Island", "Build a vacation around your interests"].map((t) => (
                    <span key={`${k}-${t}`} className="marquee-text text-2xl sm:text-3xl text-[#0F2837]/30 whitespace-nowrap flex items-center gap-8 shrink-0">
                        {t} <Sparkles size={18} className="text-[#C49A45]/60" />
                    </span>
                )))}
            </div>
        </div>
    );
}

export function SectionHeading({ eyebrow, title, sub }) {
    return (
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold flex items-center gap-2"><Compass size={14} /> {eyebrow}</p>
            <h2 className="font-serif-d font-light text-4xl sm:text-5xl lg:text-6xl text-[#0F2837] mt-4 leading-[1.05]" data-testid="island-day-heading">
                Your Villa. Your Island. <em className="text-[#2D6A76]">Your Day.</em>
            </h2>
            <p className="mt-5 text-[#5C6A78] leading-relaxed">{sub}</p>
        </motion.div>
    );
}

export const SECTION_SUB = "Explore the property, discover Amelia Island and build a vacation experience around your interests.";
